import { isDefined } from '@e-socna/utils';
import type {
  RetryStrategyFn,
  RetryContext,
  RetryDecision,
} from '@e-socna/better-fetch';

import {
  handleNetworkError,
  calculateMinDelayForRate,
  handleServerError,
} from '../../utils/strategy.util';
import { MAX_RETRY_DELAY_MS } from '../../proxy/constants/delay.constant';

import { GEO_RATE_LIMITS } from '../constants/rate-limits.constant';

/**
 * Create a retry strategy for geo API services
 *
 * @param serviceType - Type of geo service (adminDivision, geocodingSimple, etc.)
 * @returns Retry strategy function configured for the service rate limit
 *
 * @example
 * ```typescript
 * import { geoRetryStrategy } from '@e-socna/gis';
 *
 * // For admin division API (10 req/s)
 * const data = await betterFetch(url, {}, {
 *   retryStrategy: geoRetryStrategy('adminDivision'),
 * });
 * ```
 */

export const geoRetryStrategy =
  (
    serviceType: keyof typeof GEO_RATE_LIMITS = 'adminDivision',
  ): RetryStrategyFn =>
  ({ statusCode, attempt, retryAfterMs }: RetryContext): RetryDecision => {
    // Network errors and timeouts
    if (typeof statusCode === 'undefined') {
      return handleNetworkError(attempt);
    }

    // 429 Rate Limit
    if (statusCode === 429) {
      if (isDefined(retryAfterMs)) {
        return {
          shouldRetry: true,
          delayMs: Math.min(retryAfterMs, MAX_RETRY_DELAY_MS),
        };
      }

      // Calculate delay based on service rate limit
      const rateLimit = GEO_RATE_LIMITS[serviceType];
      const minDelay = calculateMinDelayForRate(rateLimit);
      const backoffDelay = minDelay * 2 ** attempt;

      return {
        shouldRetry: true,
        delayMs: Math.min(backoffDelay, MAX_RETRY_DELAY_MS),
      };
    }

    // 5xx Server errors
    if (statusCode >= 500 && statusCode < 600) {
      return handleServerError(attempt);
    }

    return { shouldRetry: false, delayMs: 0 };
  };
